import { inject, Injectable } from '@angular/core';
import { EngagementAnalysis } from '@ex/module/core/data-models/engagement-analysis';
import { EngagementPreviousData } from '@ex/module/core/data-models/engagement-previous-data';
import { BenchmarkIconEnum } from '@ex/module/shared/enums/benchmark-icon';
import { BenchmarkPayload, ProductBenchmarkRequest } from '../../product-benchmark.component';
import { EngagementDriver } from '@ex/module/core/data-models/engagement-driver';
import { HappinessPreviousData } from '@ex/module/core/data-models/happinessPreviousData';
import { Entity } from '@ex/module/core/data-models/Entity';
import { KeyResolverService } from '@ex/module/core/services/key-resolver.service';
import FactorData from '../models/factor-data';
import { BehaviorSubject } from 'rxjs';
import BenchmarkResponse from '../models/benchmark-response';

@Injectable({
  providedIn: null
})
export class ProductBenchmarkService {
  private keyResolverService = inject(KeyResolverService);

  private benchmarkData = new BehaviorSubject<BenchmarkResponse>({
    payload: null,
    request: {} as ProductBenchmarkRequest,
    response: []
  } as BenchmarkResponse);

  benchmarkData$ = this.benchmarkData.asObservable();

  setBenchmarkData(factors: BenchmarkResponse) {
    this.benchmarkData.next(factors);
  }

  calculateBenchmark(
    factors: FactorData[],
    previousData: EngagementPreviousData,
    payload: BenchmarkPayload
  ): any {

    factors.forEach(factor => {
      factor.benchmarkData = [];

      for (const sourceKey of Object.keys(payload)) {

        if (sourceKey === 'previous') {

          if (factor.key === 'overall' || factor.key === 'Overall') {
            const val = previousData.totalScore ? Number(previousData.totalScore) : -1000;
            const diff = previousData.totalScoreDiff ? Number(previousData.totalScoreDiff) : (val !== -1000 ? this.getDiff(factor.value, val) : -1000);
            factor.benchmarkData.push(
              {
                key: factor.key,
                benchmarkType: 'previous',
                value: val,
                benchmarkDiff: diff,
                icon: BenchmarkIconEnum.previous
              }
            )
          }
          else if (factor.key === 'enps' || factor.key === 'Enps') {
            const val = previousData['Enps'] ? Number(previousData['Enps']) : -1000;
            const diff = previousData['EnpsDiff'] ? Number(previousData['EnpsDiff']) : (val !== -1000 ? this.getDiff(factor.value, val) : -1000);
            factor.benchmarkData.push({
              benchmarkType: 'previous',
              value: val,
              benchmarkDiff: diff,
              icon: BenchmarkIconEnum.previous
            });
          }
          else {
            const val = this.getPreviousDriverBenchmark(factor, 'value', previousData);
            const diff = this.getPreviousDriverBenchmark(factor, 'diff', previousData);
            factor.benchmarkData.push({
              benchmarkType: 'previous',
              value: val,
              benchmarkDiff: diff !== -1000 ? diff : (val !== -1000 ? this.getDiff(factor.value, val) : -1000),
              icon: BenchmarkIconEnum.previous
            });
          }



        } else {

          const factorValues = payload[sourceKey]?.factorValues;
          const factorValue = factorValues?.find(f => f.key === factor.key);

          if (!factorValue) {
            continue;
          }

          console.log('factor: ', factor);
          

          // for factors
          factor.benchmarkData.push({
            benchmarkType: sourceKey,
            value: factorValue.value,
            topPerformer: factorValue.topPerformer,
            topPerformerDiff: this.getDiff(factor.value, factorValue.topPerformer),
            benchmarkDiff: this.getDiff(factor.value, factorValue.value),
            icon: BenchmarkIconEnum[sourceKey]
          })

        }



      }

    });

    return factors;
  }
  getPreviousDriverBenchmark(
    factor: any,
    typeOfReturn: string,
    previousData: EngagementPreviousData | HappinessPreviousData
  ) {
    if (!factor) return -1000;

    const lookup = (k: string) => previousData[k] || previousData[k.charAt(0).toUpperCase() + k.slice(1)] || previousData[k.toLowerCase()];

    if (typeOfReturn === 'value') {
      const foundValue = lookup(factor.key);
      if (foundValue !== undefined) return Number(foundValue);

      // Fallback to name-based matching
      const baseName = factor.name.replace(/\s+/g, '');
      const nameVal = lookup(baseName);
      return nameVal !== undefined ? Number(nameVal) : -1000;
    } else {
      const foundDiff = lookup(`${factor.key}Diff`);
      if (foundDiff !== undefined) return Number(foundDiff);

      // Fallback to name-based matching
      const baseName = factor.name.replace(/\s+/g, '');
      const nameDiff = lookup(`${baseName}Diff`);
      return nameDiff !== undefined ? Number(nameDiff) : -1000;
    }
  }
  getDiff(total: number, value: number) {
    return Number(total - value);
  }
  createBenchmarkRequest(params: {
    entity: Entity;
    productType: string;
    previousData?: EngagementPreviousData | HappinessPreviousData;
    language?: string;
  }): ProductBenchmarkRequest {
    return {
      industryText: this.getTranslation('Industry', params.entity.industry, params.language),
      regionOrCountryText: this.getTranslation('Country', params.entity.headQuarter, params.language),
      industry: params.entity.industry,
      regionOrCountry: params.entity.headQuarter,
      productType: params.productType,
      showMENA: true,
      showPrevious: !!params.previousData,
    } as ProductBenchmarkRequest;
  }

  getTranslation(type: string, key: string, language: string) {
    var resolver = this.keyResolverService.getResolver(type);
    return resolver?.resolve(key, language) ?? key;
  }
}
